import {
  computed,
  defineStore,
  ref,
  storeFactory,
  useCategories,
  useSupabase,
  type GetterType,
} from "#imports";
import type { QueryData } from "@supabase/supabase-js";
import { validate as validateUUID } from "uuid";
import type { Tables } from "~~/types/database.types";

export type SpotVisibility = Tables<"spots">["visibility"];
export type Spot = GetterType<ReturnType<typeof useSpots>["getById"]>;

export const useSpots = defineStore("spots", () => {
  const supabase = useSupabase();
  const categoryStore = useCategories();

  const selectSpots = (withCount: boolean = false) =>
    supabase
      .from("spots")
      .select(
        `*, place_id, maps!inner (id, cover_image_url, profiles!maps_user_id_fkey!inner (id))`,
        withCount ? { count: "exact" } : undefined,
      )
      .throwOnError();

  type RawSpot = QueryData<ReturnType<typeof selectSpots>>[number];

  const spotRawRow = (spot: RawSpot) => ({
    ...spot,
    cover_image_url: spot.cover_image_url ?? spot.maps.cover_image_url,
    categories: categoryStore.getById(spot.category_id).value,
    creatorId: spot.maps.profiles.id,
  });

  const {
    dataById,
    fetchFactory,
    fetchOneByKeyFactory,
    fetchManyByKeysFactory,
    fetchById,
    fetchByIds,
    getById,
    getByIds,
  } = storeFactory<RawSpot, ReturnType<typeof spotRawRow>>(
    "spots",
    async (payload) => await selectSpots().in("id", payload),
    spotRawRow,
  );

  const {
    metadata: metadataRecentSpots,
    fetch: fetchRecentSpots,
    result: recentSpots,
  } = fetchFactory<{ limit: number }>(
    async (payload) => await selectSpots().limit(payload.limit),
  );

  const {
    metadata: metadataSimilarSpots,
    fetch: fetchSimilarSpots,
    result: similarSpots,
  } = fetchFactory<{ limit: number; category_id: string; user_id: string }>(
    async (payload) =>
      await selectSpots()
        .limit(payload.limit)
        .order("created_at", { ascending: false })
        .neq("maps.profiles.id", payload.user_id)
        .eq("category_id", payload.category_id),
  );

  const {
    metadata: metadataMapSpots,
    fetch: fetchMapSpots,
    result: mapSpots,
    totalRows: mapSpotsTotalRows,
  } = fetchFactory<{
    map_id: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(
    async (payload) =>
      await selectSpots(payload.withTotal)
        .order("created_at", { ascending: false })
        .eq("map_id", payload.map_id)
        .range(
          payload.offset ?? 0,
          (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
        ),
  );

  /** Similar as above, but for array of mapids */
  const {
    metadata: metadataMapsSpots,
    fetch: fetchMapsSpots,
    result: mapsSpots,
  } = fetchFactory<{ limit?: number; map_ids: string[] }>(
    async (payload) =>
      await selectSpots()
        .order("created_at", { ascending: false })
        .in("map_id", payload.map_ids),
  );

  /** All the spots of a creator or multiple creators - accepts array of ids */
  const {
    metadata: metadataCreatorSpots,
    fetch: fetchCreatorSpots,
    result: creatorSpots,
  } = fetchFactory<{ creator_id: string }>(
    async (payload) =>
      await selectSpots()
        .order("created_at", { ascending: false })
        .eq("maps.profiles.id", payload.creator_id),
  );

  const {
    metadata: metadataBySlugSpot,
    fetch: fetchBySlug,
    result: spotBySlug,
    keyToId: slugToId,
  } = fetchOneByKeyFactory(
    "current_slug",
    async (current_slug) =>
      await selectSpots().eq("current_slug", current_slug).single(),
  );

  const getBySlug = (slug: string) =>
    computed(() => {
      const id = slugToId.value[slug];
      return getById(id).value;
    });

  const spotBySlugOrId = ref<GetterType<typeof getById>>();
  const fetchBySlugOrId = async (
    slug_or_id: string,
    forceRefetch: boolean = false,
  ) => {
    if (validateUUID(slug_or_id)) {
      await fetchById(slug_or_id, forceRefetch);
      spotBySlugOrId.value = getById(slug_or_id).value;
    } else {
      await fetchBySlug(slug_or_id, forceRefetch);
      spotBySlugOrId.value = spotBySlug.value;
    }
    return spotBySlugOrId;
  };

  const {
    metadata: metadataSearch,
    fetch: search,
    result: searchResults,
    totalRows: searchTotalRows,
  } = fetchFactory<{
    search: string;
    map_id?: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  }>(async (payload) => {
    let query = selectSpots(payload.withTotal)
      .textSearch("fts", payload.search, { type: "websearch" })
      .range(
        payload.offset ?? 0,
        (payload.offset ?? 0) + (payload.limit ?? 20) - 1,
      );
    if (payload.map_id) {
      query = query.eq("map_id", payload.map_id);
    }
    return await query;
  });

  const {
    metadata: metadataByPlace,
    fetch: fetchByPlace,
    result: spotByPlace,
  } = fetchFactory<{ placeId: string; limit: number }>(async (payload) => {
    return await selectSpots()
      .order("created_at", { ascending: false })
      .eq("place_id", payload.placeId)
      .limit(payload.limit);
  });

  const {
    metadata: metadataByGeohashes,
    fetch: fetchByGeohashes,
    result: spotByGeohashes,
  } = fetchManyByKeysFactory("geohash", async (geohashes) => {
    return await selectSpots().in("geohash", geohashes);
  });

  // we must return all refs!
  // we won't use them in our code, but nuxt requires it for SSR
  return {
    dataById, // do not use, only for nuxt SSR cache transfer

    fetchById,
    getById,

    fetchByIds,
    getByIds,

    metadataRecentSpots,
    fetchRecentSpots,
    recentSpots,

    metadataSimilarSpots,
    fetchSimilarSpots,
    similarSpots,

    metadataMapSpots,
    fetchMapSpots,
    mapSpots,

    metadataMapsSpots,
    fetchMapsSpots,
    mapsSpots,
    mapSpotsTotalRows,

    metadataBySlugSpot,
    fetchBySlug,
    spotBySlug,
    getBySlug,

    metadataCreatorSpots,
    fetchCreatorSpots,
    creatorSpots,
    spotBySlugOrId,
    fetchBySlugOrId,

    metadataSearch,
    search,
    searchResults,
    searchTotalRows,

    metadataByPlace,
    fetchByPlace,
    spotByPlace,

    metadataByGeohashes,
    fetchByGeohashes,
    spotByGeohashes,
  };
});
